// A tag's attributes, with quoted values allowed to contain `>`. Assumes the
// attributes are quoted and escaped, as Builder's editor writes them; a stray
// quote (title=it's) can swallow text up to the next matching quote.
const ATTRS = `(?:[^>"']|"[^"]*"|'[^']*')*`;

const ANCHOR_TAG = new RegExp(`</?a\\b${ATTRS}>`, "gi");
const ANY_TAG = new RegExp(`<${ATTRS}>`, "g");
const BLOCK_TAG = new RegExp(
  `</?(?:p|div|ul|ol|li|dl|dt|dd|h[1-6]|blockquote|pre|hr|address|table|caption|thead|tbody|tfoot|tr|td|th|section|article|aside|header|footer|nav|main|figure|figcaption|details|summary)\\b${ATTRS}>`,
  "gi",
);
// Non-breaking / zero-width spaces, as entities or characters.
const INVISIBLE =
  /&(?:nbsp|ensp|emsp|thinsp|zwsp|#160|#x0*a0|#8203|#x200b);?|[\u00a0\u200b-\u200d\ufeff]/gi;

// Rich text this long isn't authored copy; leave it alone rather than run the
// tag patterns over it (they rescan on malformed input).
const MAX_LENGTH = 10_000;

/**
 * Removes `<a>` / `</a>` tags from Builder rich text and keeps their text. The
 * COM-945 components render the rich text inside their own link, and an anchor
 * inside an anchor is invalid HTML.
 */
export const stripAnchors = (html?: string): string => {
  const value = html ?? "";
  return value.length > MAX_LENGTH ? value : value.replace(ANCHOR_TAG, "");
};

/** Replaces every block-level tag (p, div, lists, headings, tables…) with `separator`. */
export const replaceBlockTags = (html: string, separator: string): string =>
  html.length > MAX_LENGTH ? html : html.replace(BLOCK_TAG, separator);

/** True when the HTML shows any text: "<p></p>", "<p>&nbsp;</p>" and "<br>" don't. */
export const hasVisibleText = (html?: string): boolean => {
  const value = html ?? "";
  if (value.length > MAX_LENGTH) return true;
  return value.replace(ANY_TAG, "").replace(INVISIBLE, "").trim() !== "";
};

const SEPARATOR = "\uE000";

/**
 * Builder's rich text is block markup (`<p>` per line, sometimes lists), which
 * is not allowed inside a heading. Block tags become one `<br>` between lines
 * and are dropped; inline markup (bold, italic, spans, `<br>`) is kept. Links
 * lose their `<a>` (text kept) unless `keepLinks`: Feature Hero is already a
 * link. Empty rich text ("<p></p>") gives `undefined`, so no empty heading.
 */
export const toInlineHeadingHtml = (
  html?: string,
  { keepLinks = false }: { keepLinks?: boolean } = {},
): string | undefined => {
  const source = (keepLinks ? (html ?? "") : stripAnchors(html)).trim();
  const inline = replaceBlockTags(source, SEPARATOR)
    .replace(/(?:\s*\uE000\s*)+/g, SEPARATOR)
    .replace(/^\uE000|\uE000$/g, "")
    .replace(/\uE000/g, "<br>")
    // A blank first or last line (Enter left at the end) adds no height.
    .replace(/^(?:\s*<br\s*\/?>)+|(?:<br\s*\/?>\s*)+$/gi, "")
    .trim();
  return hasVisibleText(inline) ? inline : undefined;
};

/** Plain text (no markup) → HTML: escaped, with each newline as `<br>`. */
export const plainTextToHtml = (text?: string): string =>
  (text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\r?\n/g, "<br>");

// Builder rich text always starts with a block tag (`<p>`); older plain text
// that merely contains a `<word>` stays plain.
const RICH_TEXT_START = /^\s*<(?:p|div|h[1-6]|ul|ol|blockquote|pre)\b/i;

/**
 * A heading's authored content as inline HTML for ONE heading element. Rich
 * text lines (each a `<p>`) become `<br>`s instead of separate blocks. Plain
 * text (the Heading block's pre-COM-884 `longText`) is escaped and its
 * newlines become `<br>`s. Gives "" when there's nothing to show.
 */
export const toHeadingHtml = (
  content?: string,
  { keepLinks = false }: { keepLinks?: boolean } = {},
): string =>
  RICH_TEXT_START.test(content ?? "")
    ? (toInlineHeadingHtml(content, { keepLinks }) ?? "")
    : plainTextToHtml(content?.trim());
